import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { showToast } from 'shared/ui'
import { TableStore } from 'shared/ui/TableNew'
import { logger } from 'shared/lib'
import { CallApi } from 'entities/Call'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type Attachment } from 'entities/Attachment/model/Attachment'
import { DropsRow } from 'entities/Call/model/DropsRow'
import { type IAudioFileSetting } from 'entities/Settings/api/types'
import { RecordAudioStore } from 'widgets/RecordAudio'
import { TextToSpeechModalContent, TextToSpeechWidgetStore } from 'widgets/TextToSpeech'
import { RenameModalStore } from 'widgets/RenameModal'
import { VoicemailRecordAudioModal } from 'pages/settings/pages/personal/pages/account/pages/voicemailDrop/ui/VoicemailRecordAudioModal/VoicemailRecordAudioModal'

export class VoicemailDropStore {
  private _modalId = nanoid()
  private _loading = false
  private _initialLoading = true
  private _dropsMap: Map<number, DropsRow> = new Map()
  private _settingsAudioFilesMap: Map<number, IAudioFileSetting> = new Map()
  private _voicemailDropActive = false
  private readonly _tableStore: TableStore<DropsRow>

  textToSpeechStore: TextToSpeechWidgetStore | null = null
  recordAudioStore: RecordAudioStore = new RecordAudioStore({
    AUDIO_RECORD_LIMIT: 180,
    isPauseOnLimit: true,
  })

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._tableStore = new TableStore({
      key: 'voicemail_drops',
    })
  }

  init = () => {
    this.syncFromUserSettings()
    this._initialLoading = false
  }

  syncFromUserSettings = () => {
    const voicemailDropSettings = this._entities.userSettingsStore.getItem('voicemail-drop')
    if (!voicemailDropSettings) return

    const { settings } = voicemailDropSettings

    if (settings.audiofiles && Array.isArray(settings.audiofiles)) {
      this.setData(settings.audiofiles)
    }

    this._voicemailDropActive = !!settings.active
  }

  setData = (data: IAudioFileSetting[]) => {
    this._dropsMap.clear()
    this._settingsAudioFilesMap.clear()

    const rows = data.map((item) => new DropsRow(item))
    rows.forEach((item) => {
      this._dropsMap.set(item.id, item)
    })

    data.forEach((item) => {
      this._settingsAudioFilesMap.set(item.id, item)
    })

    this._tableStore.setRows(rows)
  }

  initRecordAudio = () => {
    this.recordAudioStore.init({
      handleFinish: (file: File) => {
        this.closeRecordModal()
        new RenameModalStore({
          title: 'Audio name',
          element: 'Audio',
          onSave: async (name: string) => {
            const audio = new File([file], `${name}.mp3`, {
              type: file.type,
              lastModified: file.lastModified,
            })

            await this.saveVoicemailAudio(audio, name)
          },
        })
      },
    })
  }

  closeRecordModal = () => {
    modalStore.removeModal(this._modalId)
  }

  saveVoicemailAudio = async (file: File, name: string) => {
    try {
      if (this._loading) return

      runInAction(() => {
        this._loading = true
        this._tableStore.setLoading(true)
      })

      const formData = new FormData()
      formData.append('file', file)
      formData.append('name', name)
      formData.append('type', 'undefined')

      const { data } = await CallApi.uploadVoicemailDrops(formData)

      if (data) {
        await this._entities.userSettingsStore.updateSetting({
          featureKey: 'voicemail-drop',
          settingsKey: 'audiofiles',
          value: [data, ...Array.from(this._settingsAudioFilesMap.values())],
        })
      }

      this.syncFromUserSettings()

      showToast({
        type: 'success',
        title: 'Audio saved successfully',
      })
    } finally {
      runInAction(() => {
        this._loading = false
        this._tableStore.setLoading(false)
      })
    }
  }

  handleRecordAudio = () => {
    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      title: 'Record audio',
      ModalContent: () => <VoicemailRecordAudioModal store={this} />,
      pureContent: true,
      width: 480,
      onClose: () => this.closeRecordModal(),
    })
  }

  handleMediaLibrarySelect = async (attachment: Attachment) => {
    const name = attachment.name || 'voicemail-audio'
    const source = attachment.source

    if (!source) return

    try {
      const response = await fetch(source)
      const blob = await response.blob()
      const file = new File([blob], `${name}.mp3`, { type: blob.type })

      await this.saveVoicemailAudio(file, name)
    } catch (e) {
      console.error(e)
      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again.',
      })
    }
  }

  handleTextToSpeech = () => {
    const onClose = () => {
      this.textToSpeechStore = null
      modalStore.removeModal(this._modalId)
    }

    const onCreateSpeech = (file: File) => {
      void this.saveVoicemailAudio(file, file.name.replace(/\.mp3$/i, '') || 'voicemail-audio')
      onClose()
    }

    this.textToSpeechStore = new TextToSpeechWidgetStore(
      {
        type: 'create',
        onClose,
        onCreateSpeech,
        group: 'voicemail',
      },
      this._entities
    )

    const textToSpeechStore = this.textToSpeechStore

    if (!textToSpeechStore) return

    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      title: 'Text to speech',
      ModalContent: () => <TextToSpeechModalContent store={textToSpeechStore} onClose={onClose} />,
      pureContent: true,
      width: 480,
      onClose,
    })
  }

  toggleVoicemailDrop = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      await this._entities.userSettingsStore.updateSetting({
        featureKey: 'voicemail-drop',
        settingsKey: 'active',
        value: !this._voicemailDropActive,
      })

      this.syncFromUserSettings()

      showToast({
        type: 'success',
        title: 'Settings saved.',
      })
    } catch (e) {
      logger.error(e)

      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again.',
      })
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  onRename = async (id: number) => {
    const audioSettings = this._dropsMap.get(id)

    new RenameModalStore({
      element: 'File',
      isRenamedToast: true,
      name: audioSettings?.name || '',
      onSave: async (name: string) => {
        try {
          runInAction(() => {
            this._loading = true
            this._tableStore.setLoading(true)
          })

          const { data } = await CallApi.updateVoicemailDropName(id, { name: name })
          const audioSetting = this._settingsAudioFilesMap.get(data.id)

          if (data && audioSetting) {
            audioSetting.name = data.name

            await this._entities.userSettingsStore.updateSetting({
              featureKey: 'voicemail-drop',
              settingsKey: 'audiofiles',
              value: Array.from(this._settingsAudioFilesMap.values()),
            })
          }

          this.syncFromUserSettings()
        } finally {
          runInAction(() => {
            this._loading = false
            this._tableStore.setLoading(false)
          })
        }
      },
    })
  }

  onDelete = async (id: number) => {
    try {
      runInAction(() => {
        this._loading = true
        this._tableStore.setLoading(true)
      })

      await CallApi.deleteVoicemailDrop(id)
      this._settingsAudioFilesMap.delete(id)

      await this._entities.userSettingsStore.updateSetting({
        featureKey: 'voicemail-drop',
        settingsKey: 'audiofiles',
        value: Array.from(this._settingsAudioFilesMap.values()),
      })

      this.syncFromUserSettings()

      showToast({
        type: 'success',
        title: 'File deleted',
      })
    } catch (e) {
      logger.error(e)

      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again.',
      })
    } finally {
      runInAction(() => {
        this._loading = false
        this._tableStore.setLoading(false)
      })
    }
  }

  get tableStore() {
    return this._tableStore
  }

  get drops() {
    return Array.from(this._dropsMap.values())
  }

  get initialLoading() {
    return this._initialLoading
  }

  get loading() {
    return this._loading
  }

  get isEmpty() {
    return this.drops.length === 0 && !this._initialLoading
  }

  get voicemailDropActive() {
    return this._voicemailDropActive
  }
}
